import { ORDER } from "~/common/constants";
import {
  CompletePatientBillingNoteRequest,
  GetPatientBillingNoteRequest,
  PatientBillingNote,
  UpdatePatientBillingNoteRequest,
} from "~/providers/utils/types/patientBillingNoteTypes";
import {
  GetPatientZoomMeetingRequest,
  GetPatientZoomMeetingsRequest,
  PatientSoapNote,
  UpdatePatientZoomMeetingSessionTypeRequest,
  UpsertPatientSoapNoteRequest,
  ZoomMeeting,
  ZoomMeetingListResponse,
  ZoomMeetingRecordingResponse,
  ZoomMeetingSummaryResponse,
  ZoomMeetingTranscriptResponse,
} from "~/providers/utils/types/zoomMeetingTypes";
import { api } from "./axios";

export const getPatientZoomMeetings = async ({
  patientId,
  page = 1,
  page_size = 100,
  order_by = "scheduled_start_time",
  order = ORDER.descendant,
}: GetPatientZoomMeetingsRequest) => {
  const { data } = await api.get<ZoomMeetingListResponse>(
    `/v1/team-members/patients/${patientId}/zoom-meetings`,
    {
      params: {
        page,
        page_size,
        order_by,
        order,
      },
    },
  );
  return data;
};

export const getPatientZoomMeeting = async ({
  patientId,
  sessionId,
}: GetPatientZoomMeetingRequest) => {
  const { data } = await api.get<ZoomMeeting>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}`,
  );
  return data;
};

export const getPatientZoomMeetingTranscript = async ({
  patientId,
  sessionId,
}: GetPatientZoomMeetingRequest) => {
  const { data } = await api.get<ZoomMeetingTranscriptResponse>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}/transcript`,
  );
  return data;
};

export const getPatientZoomMeetingRecording = async ({
  patientId,
  sessionId,
}: GetPatientZoomMeetingRequest) => {
  const { data } = await api.get<ZoomMeetingRecordingResponse>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}/recording`,
  );
  return data;
};

export const getPatientZoomMeetingSummary = async ({
  patientId,
  sessionId,
}: GetPatientZoomMeetingRequest) => {
  const { data } = await api.get<ZoomMeetingSummaryResponse>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}/summary`,
  );
  return data;
};

export const getPatientZoomMeetingSoapNote = async ({
  patientId,
  sessionId,
}: GetPatientZoomMeetingRequest) => {
  const { data } = await api.get<PatientSoapNote | null>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}/soap-note`,
  );
  return data;
};

export const upsertPatientZoomMeetingSoapNote = async ({
  patientId,
  sessionId,
  data,
}: UpsertPatientSoapNoteRequest) => {
  const response = await api.put<PatientSoapNote>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}/soap-note`,
    data,
  );
  return response.data;
};

export const updatePatientZoomMeetingSessionType = async ({
  patientId,
  sessionId,
  session_type,
}: UpdatePatientZoomMeetingSessionTypeRequest) => {
  const { data } = await api.patch<ZoomMeeting>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}`,
    { session_type },
  );
  return data;
};

export const getPatientZoomMeetingBillingNote = async ({
  patientId,
  sessionId,
}: GetPatientBillingNoteRequest) => {
  const { data } = await api.get<PatientBillingNote | null>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}/billing-note`,
  );
  return data;
};

export const updatePatientZoomMeetingBillingNote = async ({
  patientId,
  sessionId,
  data,
}: UpdatePatientBillingNoteRequest) => {
  const response = await api.put<PatientBillingNote>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}/billing-note`,
    { data },
  );
  return response.data;
};

export const completePatientZoomMeetingBillingNote = async ({
  patientId,
  sessionId,
  data,
}: CompletePatientBillingNoteRequest) => {
  const response = await api.post<PatientBillingNote>(
    `/v1/team-members/patients/${patientId}/zoom-meetings/${sessionId}/billing-note/complete`,
    { data },
  );
  return response.data;
};
